import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import icons from "helpers/icons";
import MainCard from "../../components/layout/MainCard/MainCard";
import SidebarHeader from "../../components/layout/WithFixedSidebar/SidebarHeader/SidebarHeader";
import LoadingWrapper from "../../components/Loading/LoadingWrapper";
import useAPI from "../../hooks/useAPI";
import { GlobalQueries } from "../../hooks/useRefreshQuery";
import NotificationDetails from "./NotificationDetails/NotificationDetails";
import NotificationsList from "./NotificationsList/NotificationsList";
import { useTranslation } from "react-i18next";
import scss from "./NotificationsPage.module.scss";

/**
 * Show the notifications for the user and/or his parent company.
 *
 * @return {JSX.Element}
 * @constructor
 */
const NotificationsPage = () => {
    const { t } = useTranslation();

    const queryClient = useQueryClient();
    const api = useAPI();

    // Load all notifications
    const { data: notifications, ...notificationsQuery } = useQuery([GlobalQueries.Notifications], () => api.list(`/notifications`));

    // Are the any unread notifications
    const unreadNotifications = notifications?.items?.filter((notification) => !notification.seen_date).length ?? 0;

    // Active notification to show details for
    const [selectedNotification, setSelectedNotification] = useState();

    const markAllAsRead = () => {
        api.delete(`/notifications`).then(() => {
            toast.success(t("notifications-default-text-all_messages_marked_as_read"));
            queryClient.invalidateQueries(GlobalQueries.Notifications);
        });
    };

    // List of notifications in the sidebar
    const sidebarList = (
        <NotificationsList
            translateKey={"notifications-default"}
            selected={selectedNotification}
            notifications={notifications?.items}
            isLoading={notificationsQuery.isLoading}
            onSelected={setSelectedNotification}
        />
    );

    return (
        <MainCard className={scss.page}>
            <div className={`${scss.layout} ${selectedNotification ? scss.showDetail : ""}`}>
                <aside className={scss.sidebar} aria-label={"Obaveštenja"}>
                    <SidebarHeader
                        title={"Obaveštenja"}
                        action={unreadNotifications > 0 ? ["check", t("notifications-default-text-mark_all_messages_as_read"), markAllAsRead] : null}
                        icon={icons.notifications}
                    />
                    {sidebarList}
                </aside>
                <main className={scss.content}>
                    <div className={scss.detailMobileHeader}>
                        <SidebarHeader title={"Obaveštenja"} icon={icons.notifications} />
                    </div>
                    {selectedNotification && (
                        <button type="button" className={scss.mobileBack} onClick={() => setSelectedNotification(undefined)}>
                            <span aria-hidden="true">‹</span> Nazad na obaveštenja
                        </button>
                    )}
                    <LoadingWrapper isLoading={notificationsQuery.isLoading}>
                        {selectedNotification ? (
                            <NotificationDetails notificationId={selectedNotification.id} />
                        ) : (
                            <div className={scss.emptyState} role="status">
                                <span className={scss.emptyIcon} aria-hidden="true">
                                    <svg viewBox="0 0 48 48" fill="none">
                                        <path d="M34 17a10 10 0 0 0-20 0c0 12-5 12-5 17h30c0-5-5-5-5-17ZM20 39h8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M24 8v-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                    </svg>
                                </span>
                                <h2>{notifications?.items?.length ? "Izaberite obaveštenje" : "Nema obaveštenja"}</h2>
                                <p>{notifications?.items?.length ? "Izaberite obaveštenje iz liste da pročitate celu poruku." : "Trenutno nemate novih poruka."}</p>
                            </div>
                        )}
                    </LoadingWrapper>
                </main>
            </div>
        </MainCard>
    );
};

export default NotificationsPage;
